import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

const JOB_POSTS = [
  { id: 1, title: "Warehouse Loaders x3",  category: "Labor",    pay: "$22/hr", applicants: 14, filled: 1, slots: 3, urgency: "Urgent",  status: "active"  },
  { id: 2, title: "Event Setup Crew x5",   category: "Events",   pay: "$20/hr", applicants: 8,  filled: 3, slots: 5, urgency: "Today",   status: "active"  },
  { id: 3, title: "Office Deep Clean",      category: "Cleaning", pay: "$18/hr", applicants: 6,  filled: 2, slots: 2, urgency: "Flex",    status: "filled"  },
  { id: 4, title: "CDL Driver (Friday)",    category: "Driving",  pay: "$28/hr", applicants: 11, filled: 0, slots: 1, urgency: "Urgent",  status: "active"  },
  { id: 5, title: "Landscaping Crew x4",   category: "Outdoor",  pay: "$24/hr", applicants: 5,  filled: 4, slots: 4, urgency: "Flex",    status: "filled"  },
];

const APPLICANTS = [
  { id: 1, job: "Warehouse Loaders", name: "Marcus Rivera",   match: 97, skills: ["Heavy Lift", "Forklift"], status: "new",      avatar: "https://public.readdy.ai/ai/img_res/df8c397a95d8d030fd745165d9bc202a.jpg" },
  { id: 2, job: "Event Setup Crew",  name: "Tyra Johnson",    match: 91, skills: ["Events", "Physical"],    status: "new",      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20black%20woman%20smiling%20clean%20background&width=80&height=80&seq=ap2&orientation=squarish" },
  { id: 3, job: "CDL Driver",        name: "Derek Owens",     match: 99, skills: ["CDL-B", "10yrs exp"],   status: "reviewed", avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20middle%20aged%20black%20man%20confident%20clean%20background%20studio&width=80&height=80&seq=ap3&orientation=squarish" },
  { id: 4, job: "Warehouse Loaders", name: "Aisha Williams",  match: 88, skills: ["Warehouse", "OSHA"],    status: "hired",    avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20african%20american%20woman%20smiling%20natural%20studio%20light&width=80&height=80&seq=ap4&orientation=squarish" },
  { id: 5, job: "Event Setup Crew",  name: "Carlos Mendez",   match: 85, skills: ["Events", "Setup"],      status: "new",      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20latin%20man%20friendly%20clean%20background%20studio%20lighting&width=80&height=80&seq=ap5&orientation=squarish" },
];

const STATUS_BADGE: Record<string, string> = {
  new:      "bg-sky-100 text-sky-700",
  reviewed: "bg-orange-100 text-orange-700",
  hired:    "bg-green-100 text-green-700",
};

const URGENCY_COLORS: Record<string, string> = {
  Urgent: "bg-red-100 text-red-700",
  Today:  "bg-orange-100 text-orange-700",
  Flex:   "bg-gray-100 text-gray-600",
};

export default function EmployerDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"jobs" | "applicants">("jobs");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "filled">("all");

  if (!user || user.type !== "employer") {
    return (
      <div className="min-h-screen bg-[#fafafa] flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <i className="ri-lock-line text-4xl text-gray-400 mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Employer account required</h2>
            <p className="text-sm text-gray-500 mb-5">Please sign in as an Employer to view this page.</p>
            <Link to="/login" className="px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const filteredJobs = JOB_POSTS.filter(j => filterStatus === "all" || j.status === filterStatus);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-28 pb-10 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <img src={user.avatar} alt={user.name} className="w-14 h-14 rounded-xl object-cover border border-gray-100" />
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-gray-600 bg-gray-100 px-3 py-1 rounded-full mb-2">
                Employer Dashboard
              </span>
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Welcome back, <span className="text-emerald-600">{user.name.split(" ")[0]}</span>
              </h1>
              <p className="text-gray-500 text-sm">BuildFast Co. · Manhattan, NY</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/hire"
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-bold rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line" />Post a Job
            </Link>
            <Link
              to="/account/employer"
              className="px-4 py-2.5 text-sm font-bold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-line mr-1" />My Profile
            </Link>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="px-4 py-2.5 text-sm font-bold rounded-lg border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-1" />Sign Out
            </button>
          </div>
        </div>
      </section>

      {/* Dashboard Body */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left: Stat Cards + Quick Actions */}
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Active Jobs",      value: "4",   icon: "ri-briefcase-4-line",         color: "text-emerald-600", bg: "bg-emerald-50" },
                { label: "Total Applicants", value: "44",  icon: "ri-user-search-line",          color: "text-sky-600",    bg: "bg-sky-50"    },
                { label: "Workers Hired",    value: "118", icon: "ri-team-line",                 color: "text-orange-600", bg: "bg-orange-50" },
                { label: "Total Spend",      value: "$9.8k",icon: "ri-money-dollar-circle-line", color: "text-green-700",  bg: "bg-green-50"  },
                { label: "Jobs Filled",      value: "38",  icon: "ri-check-double-line",         color: "text-gray-600",   bg: "bg-gray-100"  },
                { label: "Avg Fill Time",    value: "2.4h",icon: "ri-time-line",                 color: "text-amber-600",  bg: "bg-amber-50"  },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.bg} mb-2`}>
                    <i className={`${s.icon} ${s.color} text-base`} />
                  </div>
                  <div className="text-xl font-extrabold text-gray-900">{s.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Spend Chart */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900 text-sm">Spend This Month</h3>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">$3,240</span>
              </div>
              <div className="flex items-end gap-1.5 h-20">
                {[30, 55, 40, 75, 60, 90, 50, 70, 85, 65, 80, 95, 72, 88].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-sm bg-emerald-500/20 hover:bg-emerald-500/40 transition-colors" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-400">
                <span>Mar 1</span><span>Mar 31</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Quick Actions</h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: "Post Urgent Job",      to: "/hire",              icon: "ri-flashlight-line" },
                  { label: "Browse Workers",        to: "/find-work",         icon: "ri-search-line"     },
                  { label: "Upgrade Plan",          to: "/pricing",           icon: "ri-vip-crown-line"  },
                ].map(a => (
                  <Link key={a.label} to={a.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className={`${a.icon} text-gray-400`} />{a.label}
                    <i className="ri-arrow-right-s-line ml-auto text-gray-300" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Main Panel */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
              {(["jobs", "applicants"] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-lg text-xs font-semibold capitalize transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab === "jobs" ? "My Job Posts" : "Applicants"}
                </button>
              ))}
            </div>

            {/* Jobs Tab */}
            {activeTab === "jobs" && (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                  <h3 className="font-bold text-gray-900 text-sm">Job Postings</h3>
                  <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
                    {(["all", "active", "filled"] as const).map(f => (
                      <button
                        key={f}
                        onClick={() => setFilterStatus(f)}
                        className={`px-3 py-1 rounded-md text-xs font-semibold capitalize cursor-pointer whitespace-nowrap transition-colors ${
                          filterStatus === f ? "bg-white text-gray-900" : "text-gray-500"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {filteredJobs.map(job => (
                    <div key={job.id} className="px-5 py-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-gray-900 truncate">{job.title}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${URGENCY_COLORS[job.urgency]}`}>
                            {job.urgency}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{job.category}</span>
                          <span>{job.pay}</span>
                          <span>{job.filled}/{job.slots} filled</span>
                        </div>
                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full w-32 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${(job.filled / job.slots) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-extrabold text-gray-900">{job.applicants}</div>
                        <div className="text-xs text-gray-400">applicants</div>
                      </div>
                      <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors flex-shrink-0">
                        <i className="ri-more-2-line text-gray-500 text-sm" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 border-t border-gray-100">
                  <Link to="/hire" className="flex items-center gap-2 text-xs font-semibold text-emerald-600 hover:underline">
                    <i className="ri-add-line" />Post a new job
                  </Link>
                </div>
              </div>
            )}

            {/* Applicants Tab */}
            {activeTab === "applicants" && (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">Recent Applicants</h3>
                  <span className="text-xs text-gray-500">44 total across all jobs</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {APPLICANTS.map(app => (
                    <div key={app.id} className="px-5 py-4 flex items-center gap-4">
                      <img src={app.avatar} alt={app.name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-gray-900">{app.name}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[app.status]}`}>
                            {app.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-1">{app.job}</div>
                        <div className="flex gap-1 flex-wrap">
                          {app.skills.map(s => (
                            <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-extrabold text-emerald-600">{app.match}%</div>
                        <div className="text-xs text-gray-400">match</div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 cursor-pointer transition-colors">
                          <i className="ri-check-line text-emerald-600 text-sm" />
                        </button>
                        <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 cursor-pointer transition-colors">
                          <i className="ri-close-line text-red-500 text-sm" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
